import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Users } from "lucide-react";
import { FilterSelect, uniq, SearchInput, normText } from "@/components/fly/FilterSelect";
import { api, errMsg, MEDIA_LABEL } from "@/lib/api";
import { DayTabs } from "@/components/fly/DayTabs";
import { PerformanceDialog } from "@/components/fly/PerformanceDialog";
import { ScoreBreakdownDialog } from "@/components/fly/ScoreBreakdownDialog";
import { ImportSignupsCsv } from "@/components/fly/SignupTools";

export const StartListTab = ({ onChange }) => {
  const [days, setDays] = useState([]);
  const [day, setDay] = useState("");
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [age, setAge] = useState("");
  const [school, setSchool] = useState("");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState({ open: false, perf: null });
  const [detail, setDetail] = useState(null);
  const [judges, setJudges] = useState([]);
  const [rk, setRk] = useState(0);

  useEffect(() => {
    api.get("/config").then((r) => { setDays(r.data.days); setDay((d) => (r.data.days.includes(d) ? d : r.data.days[0])); setJudges(r.data.judges); });
  }, [rk]);

  const load = () => day && api.get("/admin/performances", { params: { day } }).then((r) => setItems(r.data));
  useEffect(() => { load(); setCat(""); setAge(""); setSchool(""); }, [day, rk]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const needle = normText(q.trim());
    return items.filter((p) =>
      (!needle || normText(p.name).includes(needle) || normText(p.school).includes(needle)) &&
      (!cat || p.category === cat) && (!age || p.age_group === age) && (!school || p.school === school));
  }, [items, cat, age, school, q]);

  const remove = async (p) => {
    if (!window.confirm(`Usunąć występ "${p.name}" wraz z jego ocenami?`)) return;
    try { await api.delete(`/admin/performances/${p.id}`); toast.success("Usunięto"); load(); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-4">
      <DayTabs days={days} value={day} onChange={setDay} prefix="admin-day" />
      <div className="flex flex-wrap gap-2 mt-4">
        <SearchInput testid="admin-search-input" value={q} onChange={setQ} />
        <FilterSelect testid="admin-filter-category" className="flex-1 min-w-[200px]" value={cat} onChange={setCat} options={uniq(items.map((p) => p.category))} placeholder="Wszystkie kategorie" />
        <FilterSelect testid="admin-filter-age" className="min-w-[150px]" value={age} onChange={setAge} options={uniq(items.map((p) => p.age_group))} placeholder="Każdy wiek" />
        <FilterSelect testid="admin-filter-school" className="min-w-[180px]" value={school} onChange={setSchool} options={uniq(items.map((p) => p.school))} placeholder="Wszystkie szkoły" />
        <button data-testid="admin-add-perf-btn" onClick={() => setEdit({ open: true, perf: null })} className="h-11 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4" />Dodaj</button>
        <ImportSignupsCsv onDone={() => { setRk((x) => x + 1); onChange(); }} />
      </div>
      <div className="fly-card mt-4 overflow-x-auto hidden md:block">
        <table className="w-full text-sm" data-testid="admin-startlist-table">
          <thead className="text-left font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-white/5">
            <tr><th className="p-3">Godz.</th><th className="p-3">Zawodnik</th><th className="p-3">Kategoria</th><th className="p-3">Wiek</th><th className="p-3">Szkoła</th><th className="p-3">Multimedia</th><th className="p-3 text-center">Sędziowie</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id} data-testid={`admin-perf-row-${p.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                <td className="p-3 font-mono text-xs text-slate-500">{p.time || `#${p.order}`}</td>
                <td className="p-3 font-semibold">{p.name}{p.is_team && <Users className="inline w-3.5 h-3.5 ml-1.5 text-cyan-400" />}</td>
                <td className="p-3 text-slate-300">{p.category}</td>
                <td className="p-3 text-slate-400 whitespace-nowrap">{p.age_group}</td>
                <td className="p-3 text-slate-400">{p.school}</td>
                <td className="p-3 text-amber-300 whitespace-nowrap text-xs" data-testid={`admin-perf-media-${p.id}`}>{MEDIA_LABEL[p.media] || "—"}</td>
                <td className="p-3"><JudgeDots done={p.judges_done} judges={judges} /></td>
                <td className="p-3 whitespace-nowrap text-right">
                  <IconBtn testid={`admin-perf-view-${p.id}`} onClick={() => setDetail(p.id)}><Eye className="w-4 h-4" /></IconBtn>
                  <IconBtn testid={`admin-perf-edit-${p.id}`} onClick={() => setEdit({ open: true, perf: p })}><Pencil className="w-4 h-4" /></IconBtn>
                  <IconBtn testid={`admin-perf-delete-${p.id}`} onClick={() => remove(p)} red><Trash2 className="w-4 h-4" /></IconBtn>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={8} className="p-6 text-slate-400" data-testid="admin-startlist-empty">Brak występów.</td></tr>}
          </tbody>
        </table>
      </div>
      <ul className="md:hidden mt-4 space-y-2" data-testid="admin-startlist-mobile">
        {shown.map((p) => (
          <li key={p.id} className="fly-card p-3" data-testid={`m-admin-perf-row-${p.id}`}>
            <div className="flex items-start gap-3">
              <span className="font-mono text-xs text-slate-500 w-10 shrink-0 pt-0.5">{p.time || `#${p.order}`}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold break-words">{p.name}{p.is_team && <Users className="inline w-3.5 h-3.5 ml-1.5 text-cyan-400" />}</div>
                <div className="text-xs text-slate-400 mt-0.5">{p.category} · {p.age_group}</div>
                <div className="text-xs text-slate-500">{p.school}</div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <JudgeDots done={p.judges_done} judges={judges} />
              <div className="flex">
                <IconBtn testid={`m-admin-perf-view-${p.id}`} onClick={() => setDetail(p.id)}><Eye className="w-4 h-4" /></IconBtn>
                <IconBtn testid={`m-admin-perf-edit-${p.id}`} onClick={() => setEdit({ open: true, perf: p })}><Pencil className="w-4 h-4" /></IconBtn>
                <IconBtn testid={`m-admin-perf-delete-${p.id}`} onClick={() => remove(p)} red><Trash2 className="w-4 h-4" /></IconBtn>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <PerformanceDialog open={edit.open} perf={edit.perf} day={day} days={days} onClose={() => setEdit({ open: false, perf: null })} onSaved={() => { load(); onChange(); }} />
      <ScoreBreakdownDialog pid={detail} onClose={() => setDetail(null)} onChange={() => { load(); onChange(); }} />
    </div>
  );
};

const JudgeDots = ({ done, judges }) => (
  <div className="flex justify-center gap-1">
    {judges.map((n) => (
      <span key={n} title={`Sędzia ${n}`} className={`h-6 w-6 rounded-md text-[11px] font-mono flex items-center justify-center ${done.includes(n) ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-white/5 text-slate-600 border border-white/5"}`}>{n}</span>
    ))}
  </div>
);

const IconBtn = ({ children, onClick, red, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`h-10 w-10 md:h-8 md:w-8 inline-flex items-center justify-center rounded-lg transition-colors ${red ? "text-red-400 hover:bg-red-500/10" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>{children}</button>
);
